// src/pages/health/ResultDayDetail.jsx
import { useMemo, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { format, parseISO, isValid } from "date-fns";
import { ko } from "date-fns/locale";
import BottomNav from "../../../components/common/BottomNav";
import AuthCard from "../../../components/auth/AuthCard";
import { useLoadingGate } from "../../../hooks/useLoadingGate";
import { useQuery } from "@tanstack/react-query";
import { HealthMetricsAPI } from "../../../api/healthMetrics";

import {
    LineChart, Line, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid, Legend,
    BarChart, Bar,
} from "recharts";

const RESULT_TYPES = {
    cardio_endurance: "심폐지구력",
    triple_combo: "3종 복합 운동",
    strength: "근력",
    power: "근파워",
    muscular_endurance: "근지구력",
};

/* ------------ 유틸 ------------- */
const hashStringToInt = (str) => {
    let h = 1779033703 ^ str.length;
    for (let i = 0; i < str.length; i++) {
        h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    return h >>> 0;
};
const mulberry32 = (a) => () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const lerp = (a, b, t) => a + (b - a) * t;
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

const EXERCISES = {
    bench: { label: "벤치 프레스" },
    deadlift: { label: "데드 리프트" },
    squat: { label: "스쿼트" },
};

/* --------- cardio(dummy fallback) --------- */
function makeBpmSeries(dateObj, typeLabel) {
    const BASE_MIN = 60, BASE_MAX = 180, TARGET = 130;
    const WALK_PLATEAU_STEP = 1.0, WALK_PLATEAU_MAX = 5;
    const WALK_EDGE_STEP = 0.5, WALK_EDGE_MAX = 2.5;
    const SINE_AMP_PLATEAU = 2.0, SINE_AMP_EDGE = 0.9;
    const SINE_PERIOD_SEC = 10;

    const seed = hashStringToInt(`${format(dateObj, "yyyy-MM-dd")}-${typeLabel}`);
    const rnd = mulberry32(seed);
    const phase = rnd() * Math.PI * 2;

    let drift = 0;
    const raw = [];
    for (let sec = 0; sec <= 240; sec++) {
        let base;
        if (sec <= 60) {
            const t = sec / 60;
            base = lerp(60, TARGET, easeInOut(t));
            drift = clamp(drift + (rnd() - 0.5) * WALK_EDGE_STEP, -WALK_EDGE_MAX, WALK_EDGE_MAX);
            base += drift + Math.sin((2 * Math.PI / SINE_PERIOD_SEC) * sec + phase) * SINE_AMP_EDGE;
        } else if (sec <= 180) {
            base = TARGET;
            drift = clamp(drift + (rnd() - 0.5) * WALK_PLATEAU_STEP, -WALK_PLATEAU_MAX, WALK_PLATEAU_MAX);
            base += drift + Math.sin((2 * Math.PI / SINE_PERIOD_SEC) * sec + phase) * SINE_AMP_PLATEAU;
        } else {
            const t = (sec - 180) / 60;
            base = lerp(TARGET, 60, easeInOut(t));
            drift = clamp(drift + (rnd() - 0.5) * WALK_EDGE_STEP, -WALK_EDGE_MAX, WALK_EDGE_MAX);
            base += drift + Math.sin((2 * Math.PI / SINE_PERIOD_SEC) * sec + phase) * SINE_AMP_EDGE;
        }
        raw.push({ sec, bpm: clamp(Math.round(base), BASE_MIN, BASE_MAX) });
    }

    const win = 3;
    return raw.map((p, i) => {
        let sum = 0, cnt = 0;
        for (let k = -win; k <= win; k++) {
            const q = raw[i + k];
            if (q) { sum += q.bpm; cnt++; }
        }
        return { sec: p.sec, bpm: Math.round(sum / cnt) };
    });
}

export default function ResultDayDetail() {
    const location = useLocation();
    const ready = useLoadingGate();
    const navigate = useNavigate();
    const [sp] = useSearchParams();

    const type = sp.get("type");
    const dateStr = sp.get("date");
    const typeLabel = RESULT_TYPES[type] ?? "운동 결과";

    useEffect(() => {
        if (!RESULT_TYPES[type]) navigate("/workout", { replace: true });
    }, [type, navigate]);

    const dateObj = useMemo(() => {
        const d = parseISO(dateStr || "");
        return isValid(d) ? d : new Date();
    }, [dateStr]);
    const isoDate = useMemo(() => format(dateObj, "yyyy-MM-dd"), [dateObj]);

    const titleDate = location.state?.typeLabel || RESULT_TYPES[type] || "운동 결과";

    // ✅ 선택된 타입(day)만 요청
    const { data: dayList = [] } = useQuery({
        queryKey: ["result", "day", type, isoDate],
        queryFn: () => {
            switch (type) {
                case "cardio_endurance":
                    return HealthMetricsAPI.cardio.listDay(isoDate);
                case "triple_combo":
                    return HealthMetricsAPI.tripleStrength.listDay(isoDate);
                case "strength":
                    return HealthMetricsAPI.strength.listDay(isoDate);
                case "power":
                    return HealthMetricsAPI.power.listDay(isoDate);
                case "muscular_endurance":
                    return HealthMetricsAPI.muscularEndurance.listDay(isoDate);
                default:
                    return Promise.resolve([]);
            }
        },
        enabled: ready && !!RESULT_TYPES[type],
        staleTime: 60_000,
    });

    // ✅ 기존 변수명과 호환되게 매핑
    const cardioDayList = type === "cardio_endurance" ? dayList : [];
    const tripleDayList = type === "triple_combo" ? dayList : [];
    const strengthDayList = type === "strength" ? dayList : [];
    const powerDayList = type === "power" ? dayList : [];
    const meDayList = type === "muscular_endurance" ? dayList : [];

    /* ---------------- cardio ---------------- */
    const [cardioIdx, setCardioIdx] = useState(0);
    useEffect(() => { if (cardioIdx >= cardioDayList.length) setCardioIdx(0); }, [cardioDayList.length, cardioIdx]);
    const activeCardio = type === "cardio_endurance" ? cardioDayList[cardioIdx] : null;

    const bpmSeries = useMemo(() => {
        if (type !== "cardio_endurance") return [];
        if (activeCardio && Array.isArray(activeCardio.series) && activeCardio.series.length > 0) {
            const step = activeCardio.sample_interval_s || 1;
            return activeCardio.series.map((bpm, i) => ({ sec: i * step, bpm }));
        }
        return makeBpmSeries(dateObj, typeLabel);
    }, [type, activeCardio, dateObj, typeLabel]);

    const maxSec = bpmSeries.length ? bpmSeries[bpmSeries.length - 1].sec : 240;
    const xTicks = useMemo(() => {
        const arr = [];
        for (let t = 0; t <= maxSec; t += 60) arr.push(t);
        if (arr[arr.length - 1] !== maxSec) arr.push(maxSec);
        return arr;
    }, [maxSec]);

    const pickAt = (sec) => {
        if (!bpmSeries.length) return 0;
        const step = activeCardio?.sample_interval_s || 1;
        const idx = Math.min(bpmSeries.length - 1, Math.round(sec / step));
        return bpmSeries[idx]?.bpm ?? 0;
    };
    const stat60 = pickAt(60);
    const stat120 = pickAt(120);
    const stat180 = pickAt(180);

    /* ---------------- triple_combo ---------------- */
    const tripleByExercise = useMemo(() => {
        const m = new Map();
        (tripleDayList || []).forEach((row) => {
            const k = row.exercise;
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(row);
        });
        m.forEach((arr) => arr.sort((a, b) => new Date(a.dt) - new Date(b.dt)));
        return m;
    }, [tripleDayList]);

    const availableExercises = useMemo(() => Array.from(tripleByExercise.keys()), [tripleByExercise]);

    const [exerciseKey, setExerciseKey] = useState(null);
    useEffect(() => {
        if (!exerciseKey || !tripleByExercise.has(exerciseKey)) {
            const first = availableExercises[0] || "bench";
            setExerciseKey(first);
        }
    }, [exerciseKey, tripleByExercise, availableExercises]);

    const [tripleIdx, setTripleIdx] = useState(0);
    useEffect(() => { setTripleIdx(0); }, [exerciseKey]);

    const selectedRows = useMemo(
        () => (exerciseKey ? (tripleByExercise.get(exerciseKey) || []) : []),
        [exerciseKey, tripleByExercise]
    );
    useEffect(() => { if (tripleIdx >= selectedRows.length) setTripleIdx(0); }, [selectedRows.length, tripleIdx]);

    const activeTriple = selectedRows[tripleIdx] || null;

    const tripleSeries = useMemo(() => {
        if (!activeTriple) return [];
        const s1 = activeTriple.t1_series || [];
        const s2 = activeTriple.t2_series || [];
        const s3 = activeTriple.t3_series || [];
        const step = activeTriple.sample_interval_s || 1;
        const maxLen = Math.max(s1.length, s2.length, s3.length);
        const data = [];
        for (let i = 0; i < maxLen; i++) {
            const sec = i * step;
            const k1 = s1[i] ?? null;
            const k2 = s2[i] ?? null;
            const k3 = s3[i] ?? null;
            data.push({ sec, p1: k1, p2: k2, p3: k3, k1, k2, k3 });
        }
        return data;
    }, [activeTriple]);

    const tripleMaxSec = useMemo(
        () => (activeTriple?.time_sec ? activeTriple.time_sec : (tripleSeries.length ? tripleSeries[tripleSeries.length - 1].sec : 0)),
        [activeTriple, tripleSeries]
    );

    const tripleXTicks = useMemo(() => {
        if (!tripleMaxSec) return [0];
        const inc = tripleMaxSec <= 30 ? 5 : 10;
        const arr = [];
        for (let t = 0; t <= tripleMaxSec; t += inc) arr.push(t);
        if (arr[arr.length - 1] !== tripleMaxSec) arr.push(tripleMaxSec);
        return arr;
    }, [tripleMaxSec]);

    const kgMax = useMemo(() => {
        let m = 0;
        tripleSeries.forEach((d) => {
            if (d.k1 != null) m = Math.max(m, d.k1);
            if (d.k2 != null) m = Math.max(m, d.k2);
            if (d.k3 != null) m = Math.max(m, d.k3);
        });
        return Math.ceil((m || 0) / 5) * 5 || 10;
    }, [tripleSeries]);

    const tripleStats = useMemo(() => {
        if (!activeTriple) return [];
        const s1 = activeTriple.t1_series || [];
        const s2 = activeTriple.t2_series || [];
        const s3 = activeTriple.t3_series || [];
        const p1 = s1.length ? Math.max(...s1) : 0;
        const p2 = s2.length ? Math.max(...s2) : 0;
        const p3 = s3.length ? Math.max(...s3) : 0;
        return [
            { badge: "1회차", value: +p1.toFixed(1), unit: "kg", color: "#F87171" },
            { badge: "2회차", value: +p2.toFixed(1), unit: "kg", color: "#FBBF24" },
            { badge: "3회차", value: +p3.toFixed(1), unit: "kg", color: "#34D399" },
        ];
    }, [activeTriple]);

    const tripleAnalysisLines = useMemo(() => {
        if (!activeTriple) return [];
        const title = EXERCISES[activeTriple.exercise]?.label || activeTriple.exercise;
        return [
            `[${title}]`,
            `최대 근력(Peak): ${activeTriple.peak_kg?.toFixed(1)}kg`,
            `평균 근력(Average): ${activeTriple.average_kg?.toFixed(1)}kg`,
            `체중당 최대(Peak/Weight): ${activeTriple.peak_per_weight ?? "-"} kg`,
            `총 측정 시간(Time): ${activeTriple.time_sec ?? "-"}초`,
            `샘플 간격: ${activeTriple.sample_interval_s ?? 1}초`,
        ];
    }, [activeTriple]);

    /* ---------------- strength ---------------- */
    const [strengthIdx, setStrengthIdx] = useState(0);
    useEffect(() => { if (strengthIdx >= strengthDayList.length) setStrengthIdx(0); }, [strengthDayList.length, strengthIdx]);
    const activeStrength = type === "strength" ? strengthDayList[strengthIdx] : null;

    const strengthSeries = useMemo(() => {
        if (!activeStrength) return [];
        const s1 = activeStrength.t1_series || [];
        const s2 = activeStrength.t2_series || [];
        const s3 = activeStrength.t3_series || [];
        const step = activeStrength.sample_interval_s || 1;
        const maxLen = Math.max(s1.length, s2.length, s3.length);
        const data = [];
        for (let i = 0; i < maxLen; i++) {
            const sec = i * step;
            const k1 = s1[i] ?? null;
            const k2 = s2[i] ?? null;
            const k3 = s3[i] ?? null;
            data.push({ sec, p1: k1, p2: k2, p3: k3, k1, k2, k3 });
        }
        return data;
    }, [activeStrength]);

    const strengthMaxSec = useMemo(
        () => (activeStrength?.time_sec ? activeStrength.time_sec : (strengthSeries.length ? strengthSeries[strengthSeries.length - 1].sec : 0)),
        [activeStrength, strengthSeries]
    );

    const strengthXTicks = useMemo(() => {
        if (!strengthMaxSec) return [0];
        const inc = strengthMaxSec <= 30 ? 5 : 10;
        const arr = [];
        for (let t = 0; t <= strengthMaxSec; t += inc) arr.push(t);
        if (arr[arr.length - 1] !== strengthMaxSec) arr.push(strengthMaxSec);
        return arr;
    }, [strengthMaxSec]);

    const strengthKgMax = useMemo(() => {
        let m = 0;
        strengthSeries.forEach((d) => {
            if (d.k1 != null) m = Math.max(m, d.k1);
            if (d.k2 != null) m = Math.max(m, d.k2);
            if (d.k3 != null) m = Math.max(m, d.k3);
        });
        return Math.ceil((m || 0) / 5) * 5 || 10;
    }, [strengthSeries]);

    const strengthStats = useMemo(() => {
        if (!activeStrength) return [];
        const s1 = activeStrength.t1_series || [];
        const s2 = activeStrength.t2_series || [];
        const s3 = activeStrength.t3_series || [];
        const p1 = s1.length ? Math.max(...s1) : 0;
        const p2 = s2.length ? Math.max(...s2) : 0;
        const p3 = s3.length ? Math.max(...s3) : 0;
        return [
            { badge: "1회차", value: +p1.toFixed(1), unit: "kg", color: "#F87171" },
            { badge: "2회차", value: +p2.toFixed(1), unit: "kg", color: "#FBBF24" },
            { badge: "3회차", value: +p3.toFixed(1), unit: "kg", color: "#34D399" },
        ];
    }, [activeStrength]);

    const strengthAnalysisLines = useMemo(() => {
        if (!activeStrength) return [];
        return [
            "[근력]",
            `최대 근력(Peak): ${activeStrength.peak_kg?.toFixed(1)}kg`,
            `평균 근력(Average): ${activeStrength.average_kg?.toFixed(1)}kg`,
            `체중당 최대(Peak/Weight): ${activeStrength.peak_per_weight ?? "-"} kg`,
            `총 측정 시간(Time): ${activeStrength.time_sec ?? "-"}초`,
            `샘플 간격: ${activeStrength.sample_interval_s ?? 1}초`,
        ];
    }, [activeStrength]);

    /* ---------------- power ---------------- */
    const [powerIdx, setPowerIdx] = useState(0);
    useEffect(() => { if (powerIdx >= powerDayList.length) setPowerIdx(0); }, [powerDayList.length, powerIdx]);
    const activePower = type === "power" ? powerDayList[powerIdx] : null;

    const POWER_DUMMY = { trials: [883.6, 766.3, 285.2], peak: 883.6, average: Math.round(((883.6 + 766.3 + 285.2) / 3) * 10) / 10, timeSec: 9, unit: "W" };

    function makeOverlappedSeries(trials, seedKey, { length = 9, center = 3, width = 2, yBase = 0, yMax = 1000 } = {}) {
        const seed = hashStringToInt(`ol-${seedKey}-${trials.join(",")}`);
        const rnd = mulberry32(seed);
        const inMax = Math.max(1, ...trials);
        const scaleY = (w) => (w <= 0 ? yBase : yBase + (w / inMax) * (yMax - yBase));
        const shapedW = (sec, peakW) => {
            const d = Math.abs(sec - center);
            if (d > width) return 0;
            const base = peakW * (1 - d / width);
            const noise = (rnd() - 0.5) * peakW * 0.04;
            return Math.max(0, base + noise);
        };
        const data = [];
        for (let sec = 0; sec <= length; sec++) {
            const w1 = shapedW(sec, trials[0]);
            const w2 = shapedW(sec, trials[1]);
            const w3 = shapedW(sec, trials[2]);
            data.push({ sec, p1: scaleY(w1), p2: scaleY(w2), p3: scaleY(w3), w1: +w1.toFixed(1), w2: +w2.toFixed(1), w3: +w3.toFixed(1) });
        }
        return data;
    }

    const powerSeries = useMemo(() => {
        if (!activePower) return [];
        const s1 = activePower.t1_series || [];
        const s2 = activePower.t2_series || [];
        const s3 = activePower.t3_series || [];
        const step = activePower.sample_interval_s || 1;
        const maxLen = Math.max(s1.length, s2.length, s3.length);
        const data = [];
        for (let i = 0; i < maxLen; i++) {
            const sec = i * step;
            const w1 = s1[i] ?? null;
            const w2 = s2[i] ?? null;
            const w3 = s3[i] ?? null;
            data.push({ sec, p1: w1, p2: w2, p3: w3, w1, w2, w3 });
        }
        return data;
    }, [activePower]);

    const powerMaxSec = useMemo(() => {
        if (activePower?.time_sec) return activePower.time_sec;
        return powerSeries.length ? powerSeries[powerSeries.length - 1].sec : POWER_DUMMY.timeSec;
    }, [activePower, powerSeries]);

    const powerXTicks = useMemo(() => {
        if (!powerMaxSec) return [0];
        const inc = powerMaxSec <= 10 ? 3 : 5;
        const arr = [];
        for (let t = 0; t <= powerMaxSec; t += inc) arr.push(t);
        if (arr[arr.length - 1] !== powerMaxSec) arr.push(powerMaxSec);
        return arr;
    }, [powerMaxSec]);

    const powerWattsMax = useMemo(() => {
        let m = 0;
        powerSeries.forEach((d) => {
            if (d.w1 != null) m = Math.max(m, d.w1);
            if (d.w2 != null) m = Math.max(m, d.w2);
            if (d.w3 != null) m = Math.max(m, d.w3);
            if (d.p1 != null) m = Math.max(m, d.p1);
            if (d.p2 != null) m = Math.max(m, d.p2);
            if (d.p3 != null) m = Math.max(m, d.p3);
        });
        const upper = m > 0 ? m : 1000;
        return Math.ceil(upper / 50) * 50;
    }, [powerSeries]);

    const powerStats = useMemo(() => {
        if (activePower) {
            const s1 = activePower.t1_series || [];
            const s2 = activePower.t2_series || [];
            const s3 = activePower.t3_series || [];
            const p1 = s1.length ? Math.max(...s1) : 0;
            const p2 = s2.length ? Math.max(...s2) : 0;
            const p3 = s3.length ? Math.max(...s3) : 0;
            return [
                { badge: "1회차", value: +p1.toFixed(0), unit: "W", color: "#F87171" },
                { badge: "2회차", value: +p2.toFixed(0), unit: "W", color: "#FBBF24" },
                { badge: "3회차", value: +p3.toFixed(0), unit: "W", color: "#34D399" },
            ];
        }
        const t = POWER_DUMMY.trials;
        return [
            { badge: "1회차", value: Math.round(t[0]), unit: "W", color: "#F87171" },
            { badge: "2회차", value: Math.round(t[1]), unit: "W", color: "#FBBF24" },
            { badge: "3회차", value: Math.round(t[2]), unit: "W", color: "#34D399" },
        ];
    }, [activePower]);

    const powerAnalysisLines = useMemo(() => {
        if (activePower) {
            const s1 = activePower.t1_series || [];
            const s2 = activePower.t2_series || [];
            const s3 = activePower.t3_series || [];
            const all = [...s1, ...s2, ...s3];
            const peak = activePower.peak_watts ?? (all.length ? Math.max(...all) : 0);
            const average = activePower.average_watts ?? (all.length ? +(avg(all)).toFixed(1) : 0);
            return [
                "[근파워]",
                `최대 근파워(Peak): ${+peak.toFixed?.(1) ?? peak} W`,
                `평균 근파워(Average): ${average} W`,
                `체중당 최대(Peak/Weight): ${activePower.peak_per_weight ?? "-"} W/kg`,
                `총 측정 시간(Time): ${activePower.time_sec ?? "-"}초`,
                `샘플 간격: ${activePower.sample_interval_s ?? 1}초`,
            ];
        }
        const m = POWER_DUMMY;
        return [
            "[근파워]",
            `1회차 최대 근파워: ${m.trials[0]}${m.unit}`,
            `2회차 최대 근파워: ${m.trials[1]}${m.unit}`,
            `3회차 최대 근파워: ${m.trials[2]}${m.unit}`,
            `최대 근파워(Peak Power): ${m.peak}${m.unit}`,
            `평균 근파워(Average Power): ${m.average}${m.unit}`,
            `총 측정 시간(Time): ${m.timeSec}초`,
        ];
    }, [activePower]);

    /* ---------------- muscular_endurance (실데이터 연동) ---------------- */
    const [meIdx, setMeIdx] = useState(0);
    useEffect(() => { if (meIdx >= meDayList.length) setMeIdx(0); }, [meDayList.length, meIdx]);
    const activeME = type === "muscular_endurance" ? meDayList[meIdx] : null;

    const enduranceSeries = useMemo(() => {
        if (type !== "muscular_endurance") return [];
        const reps = activeME?.rep_series || [];
        return reps.map((v, i) => ({ i: i + 1, v }));
    }, [type, activeME]);

    const enduranceYMax = useMemo(() => {
        const arr = activeME?.rep_series || [];
        let m = 0;
        arr.forEach((v) => { if (v != null) m = Math.max(m, v); });
        if (m === 0) m = 40;
        return Math.ceil(m / 5) * 5;
    }, [activeME]);

    const enduranceStatsTop = useMemo(() => {
        if (!activeME) return [
            { label: "현재 진행 횟수", value: 0 },
            { label: "측정 목표 횟수", value: "-" },
            { label: "현재 측정 최대치", value: "-" },
        ];
        const repsDone = activeME.reps_done ?? (activeME.rep_series?.length || 0);
        const repsTarget = activeME.reps_target ?? "-";
        const currentMax = activeME.rep_series?.length ? Math.max(...activeME.rep_series).toFixed(1) : "-";
        return [
            { label: "현재 진행 횟수", value: repsDone },
            { label: "측정 목표 횟수", value: repsTarget },
            { label: "현재 측정 최대치", value: currentMax },
        ];
    }, [activeME]);

    const enduranceAnalysisLines = useMemo(() => {
        if (type !== "muscular_endurance") return [];
        if (!activeME) return ["데이터가 없습니다."];
        return [
            "[근지구력］",
            `반복 횟수(reps): ${activeME.reps_done ?? (activeME.rep_series?.length || 0)}회`,
            activeME.total_work_j != null ? `총 일량(Total Work): ${activeME.total_work_j}J` : null,
            activeME.endurance_pct != null ? `근지구력 %: ${activeME.endurance_pct}%` : null,
            activeME.avg_power_w != null ? `평균 파워(Average Power): ${activeME.avg_power_w}W` : null,
            `총 측정 시간(Time): ${activeME.time_sec ?? "-"}초`,
            `단위(Unit): ${activeME.unit ?? "W"}`,
        ].filter(Boolean);
    }, [type, activeME]);

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 md:px-8 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard className="flex-1 w-full mx-auto" title={titleDate} onClose={() => navigate(-1)}>
                    <div className="flex-1 px-0 md:px-0 pt-4 md:pt-5 pb-24 md:pb-28">

                        {/* ---------------- 3종 복합 운동 ---------------- */}
                        {type === "triple_combo" && (
                            <>
                                <div className="flex gap-2 md:gap-3 mb-3">
                                    {availableExercises.map((key) => {
                                        const active = key === exerciseKey;
                                        return (
                                            <button
                                                key={key}
                                                onClick={() => setExerciseKey(key)}
                                                className={[
                                                    "px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-semibold transition",
                                                    active ? "bg-[#5F9BFF] text-white shadow" : "bg-gray-200 text-gray-700",
                                                ].join(" ")}
                                            >
                                                {EXERCISES[key]?.label || key}
                                            </button>
                                        );
                                    })}
                                </div>

                                {selectedRows.length > 1 && (
                                    <div className="flex gap-2 md:gap-3 mb-3">
                                        {selectedRows.map((s, i) => (
                                            <button
                                                key={s.id ?? i}
                                                onClick={() => setTripleIdx(i)}
                                                className={[
                                                    "px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-semibold transition",
                                                    i === tripleIdx ? "bg-[#5F9BFF] text-white shadow" : "bg-gray-200 text-gray-700",
                                                ].join(" ")}
                                            >
                                                세션 {i + 1} · {format(new Date(s.dt), "HH:mm", { locale: ko })}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                <section className="rounded-2xl border border-gray-100 shadow-sm pt-4 md:pt-5 pb-2 md:pb-3 pr-5 md:pr-6">
                                    <div className="relative h-60 md:h-72 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={tripleSeries} margin={{ top: 10, right: 12, left: 0, bottom: 20 }}>
                                                <CartesianGrid stroke="#D1D5DB" strokeDasharray="4 4" vertical={false} />
                                                <XAxis
                                                    dataKey="sec"
                                                    type="number"
                                                    domain={[0, Math.max(tripleMaxSec, 0)]}
                                                    ticks={tripleXTicks}
                                                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                                    axisLine={{ stroke: "#9CA3AF" }}
                                                    tickLine={{ stroke: "#9CA3AF" }}
                                                />
                                                <YAxis
                                                    type="number"
                                                    domain={[0, kgMax]}
                                                    ticks={[0, Math.round(kgMax * 0.25), Math.round(kgMax * 0.5), Math.round(kgMax * 0.75), kgMax]}
                                                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                                    axisLine={{ stroke: "#9CA3AF" }}
                                                    tickLine={{ stroke: "#9CA3AF" }}
                                                />
                                                <Tooltip
                                                    labelFormatter={(v) => `${v}s`}
                                                    formatter={(value, name) => [`${(value ?? 0).toFixed(1)} kg`, name]}
                                                />
                                                <Legend verticalAlign="bottom" align="center" iconType="plainline" iconSize={10} />
                                                <Line name="1회차" type="monotone" dataKey="p1" dot={false} stroke="#FF7575" strokeWidth={3} strokeLinecap="round" />
                                                <Line name="2회차" type="monotone" dataKey="p2" dot={false} stroke="#FFAA00" strokeWidth={3} strokeLinecap="round" />
                                                <Line name="3회차" type="monotone" dataKey="p3" dot={false} stroke="#63BF63" strokeWidth={3} strokeLinecap="round" />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </section>

                                <TrialInlineStats items={tripleStats} />

                                <section className="mt-4 md:mt-5 rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="px-4 md:px-5 py-3 md:py-4">
                                        <h3 className="font-bold md:text-lg">기능 분석</h3>
                                    </div>
                                    <div className="px-3"><div className="h-px bg-gray-100" /></div>
                                    <div className="px-3 md:px-4 py-3 md:py-4 text-sm md:text-base leading-6 text-[#9F9F9F] whitespace-pre-line">
                                        {activeTriple ? tripleAnalysisLines.join("\n") : "데이터가 없습니다."}
                                    </div>
                                </section>
                            </>
                        )}

                        {/* ---------------- cardio ---------------- */}
                        {type === "cardio_endurance" && (
                            <>
                                {cardioDayList.length > 1 && (
                                    <div className="flex gap-2 md:gap-3 mb-3">
                                        {cardioDayList.map((s, i) => (
                                            <button
                                                key={s.id ?? i}
                                                onClick={() => setCardioIdx(i)}
                                                className={[
                                                    "px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-semibold transition",
                                                    i === cardioIdx ? "bg-[#5F9BFF] text-white shadow" : "bg-gray-200 text-gray-700",
                                                ].join(" ")}
                                            >
                                                세션 {i + 1} · {format(new Date(s.dt), "HH:mm", { locale: ko })}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                <section className="rounded-2xl border border-gray-100 shadow-sm pt-4 md:pt-5 pb-2 md:pb-3 pr-5 md:pr-6">
                                    <div className="relative h-60 md:h-72 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={bpmSeries} margin={{ top: 10, right: 12, left: 0, bottom: 20 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="sec" type="number" domain={[0, maxSec]} ticks={xTicks} allowDecimals={false} tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}s`} />
                                                <YAxis domain={[60, 180]} tick={{ fontSize: 12 }} />
                                                <Tooltip formatter={(value) => [`${value} BPM`, "BPM"]} labelFormatter={(label) => `${label}s`} />
                                                <Legend
                                                    verticalAlign="bottom"
                                                    align="center"
                                                    iconType="plainline"
                                                    iconSize={10}
                                                    wrapperStyle={{ fontSize: 11, paddingTop: 2 }}
                                                    formatter={() => (
                                                        <span style={{ color: "#5F9BFF", fontWeight: 600 }}>
                                                            {activeCardio?.avg_bpm != null ? `BPM (avg ${activeCardio.avg_bpm})` : "BPM"}
                                                        </span>
                                                    )}
                                                />
                                                <Line name="BPM" type="natural" dataKey="bpm" dot={false} stroke="#5F9BFF" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </section>

                                <InlineStats
                                    items={[
                                        { label: "60초", value: stat60, unit: "BPM" },
                                        { label: "120초", value: stat120, unit: "BPM" },
                                        { label: "180초", value: stat180, unit: "BPM" },
                                    ]}
                                />

                                <section className="mt-4 md:mt-5 rounded-2xl border border-gray-100 shadow-sm">
                                    <div className="px-4 md:px-5 py-3 md:py-4">
                                        <h3 className="font-bold md:text-lg">기능 분석</h3>
                                    </div>
                                    <div className="px-3"><div className="h-px bg-gray-100" /></div>
                                    <div className="px-4 md:px-5 py-3 md:py-4 text-sm md:text-base leading-6 text-[#9F9F9F] whitespace-pre-line">
                                        {[
                                            "[심폐지구력]",
                                            `1분 심박수 : ${stat60} BPM`,
                                            `2분 심박수 : ${stat120} BPM`,
                                            `3분 심박수 : ${stat180} BPM`,
                                            `전체 평균 : ${Math.round(bpmSeries.length ? avg(bpmSeries.map((d) => d.bpm)) : 0)} BPM`,
                                        ].join("\n")}
                                    </div>
                                </section>
                            </>
                        )}

                        {/* ---------------- strength ---------------- */}
                        {type === "strength" && (
                            <>
                                {strengthDayList.length > 1 && (
                                    <div className="flex gap-2 md:gap-3 mb-3">
                                        {strengthDayList
                                            .slice()
                                            .sort((a, b) => new Date(a.dt) - new Date(b.dt))
                                            .map((s, i) => (
                                                <button
                                                    key={s.id ?? i}
                                                    onClick={() => setStrengthIdx(i)}
                                                    className={[
                                                        "px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-semibold transition",
                                                        i === strengthIdx ? "bg-[#5F9BFF] text-white shadow" : "bg-gray-200 text-gray-700",
                                                    ].join(" ")}
                                                >
                                                    세션 {i + 1} · {format(new Date(s.dt), "HH:mm", { locale: ko })}
                                                </button>
                                            ))}
                                    </div>
                                )}

                                <section className="rounded-2xl border border-gray-100 shadow-sm pt-4 md:pt-5 pb-2 md:pb-3 pr-5 md:pr-6">
                                    <div className="relative h-60 md:h-72 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={strengthSeries} margin={{ top: 10, right: 12, left: 0, bottom: 20 }}>
                                                <CartesianGrid stroke="#D1D5DB" strokeDasharray="4 4" />
                                                <XAxis
                                                    dataKey="sec"
                                                    type="number"
                                                    domain={[0, Math.max(strengthMaxSec, 0)]}
                                                    ticks={strengthXTicks}
                                                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                                    axisLine={{ stroke: "#9CA3AF" }}
                                                    tickLine={{ stroke: "#9CA3AF" }}
                                                />
                                                <YAxis
                                                    type="number"
                                                    domain={[0, strengthKgMax]}
                                                    ticks={[0, Math.round(strengthKgMax * 0.25), Math.round(strengthKgMax * 0.5), Math.round(strengthKgMax * 0.75), strengthKgMax]}
                                                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                                    axisLine={{ stroke: "#9CA3AF" }}
                                                    tickLine={{ stroke: "#9CA3AF" }}
                                                />
                                                <Tooltip
                                                    labelFormatter={(v) => `${v}s`}
                                                    formatter={(value, name) => [`${(value ?? 0).toFixed(1)} kg`, name]}
                                                />
                                                <Legend verticalAlign="bottom" align="center" iconType="plainline" iconSize={10} />
                                                <Line name="1회차" type="monotone" dataKey="p1" dot={false} stroke="#FF7575" strokeWidth={3} />
                                                <Line name="2회차" type="monotone" dataKey="p2" dot={false} stroke="#FFAA00" strokeWidth={3} />
                                                <Line name="3회차" type="monotone" dataKey="p3" dot={false} stroke="#34D399" strokeWidth={3} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </section>

                                <TrialInlineStats items={strengthStats} />

                                <section className="mt-4 md:mt-5 rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="px-4 md:px-5 py-3 md:py-4"><h3 className="font-bold md:text-lg">기능 분석</h3></div>
                                    <div className="px-3"><div className="h-px bg-gray-100" /></div>
                                    <div className="px-3 md:px-4 py-3 md:py-4 text-sm md:text-base leading-6 text-[#9F9F9F] whitespace-pre-line">
                                        {activeStrength ? strengthAnalysisLines.join("\n") : "데이터가 없습니다."}
                                    </div>
                                </section>
                            </>
                        )}

                        {/* ---------------- power ---------------- */}
                        {type === "power" && (
                            <>
                                {powerDayList.length > 1 && (
                                    <div className="flex gap-2 md:gap-3 mb-3">
                                        {powerDayList
                                            .slice()
                                            .sort((a, b) => new Date(a.dt) - new Date(b.dt))
                                            .map((s, i) => (
                                                <button
                                                    key={s.id ?? i}
                                                    onClick={() => setPowerIdx(i)}
                                                    className={[
                                                        "px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-semibold transition",
                                                        i === powerIdx ? "bg-[#5F9BFF] text-white shadow" : "bg-gray-200 text-gray-700",
                                                    ].join(" ")}
                                                >
                                                    세션 {i + 1} · {format(new Date(s.dt), "HH:mm", { locale: ko })}
                                                </button>
                                            ))}
                                    </div>
                                )}

                                <section className="rounded-2xl border border-gray-100 shadow-sm pt-4 md:pt-5 pb-2 md:pb-3 pr-5 md:pr-6">
                                    <div className="relative h-60 md:h-72 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart
                                                data={activePower ? powerSeries : makeOverlappedSeries(POWER_DUMMY.trials, "power", { length: POWER_DUMMY.timeSec, center: 3, width: 2, yBase: 0, yMax: 1000 })}
                                                margin={{ top: 10, right: 12, left: 0, bottom: 20 }}
                                            >
                                                <CartesianGrid stroke="#D1D5DB" strokeDasharray="4 4" />
                                                <XAxis
                                                    dataKey="sec"
                                                    type="number"
                                                    domain={[0, powerMaxSec]}
                                                    ticks={powerXTicks}
                                                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                                    axisLine={{ stroke: "#9CA3AF" }}
                                                    tickLine={{ stroke: "#9CA3AF" }}
                                                />
                                                <YAxis
                                                    type="number"
                                                    domain={[0, activePower ? powerWattsMax : 1000]}
                                                    ticks={
                                                        activePower
                                                            ? [0, Math.round(powerWattsMax * 0.25), Math.round(powerWattsMax * 0.5), Math.round(powerWattsMax * 0.75), powerWattsMax]
                                                            : [0, 250, 500, 750, 1000]
                                                    }
                                                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                                    axisLine={{ stroke: "#9CA3AF" }}
                                                    tickLine={{ stroke: "#9CA3AF" }}
                                                />
                                                <Tooltip
                                                    labelFormatter={(v) => `${v}s`}
                                                    formatter={(value, name, props) => {
                                                        const key = { "1회차": "w1", "2회차": "w2", "3회차": "w3" }[name] || { "1회차": "p1", "2회차": "p2", "3회차": "p3" }[name];
                                                        const v = props?.payload?.[key] ?? value ?? 0;
                                                        return [`${(v ?? 0).toFixed(1)} W`, name];
                                                    }}
                                                />
                                                <Legend verticalAlign="bottom" align="center" iconType="plainline" iconSize={10} />
                                                <Line name="1회차" type="monotone" dataKey="p1" dot={false} stroke="#FF7575" strokeWidth={3} />
                                                <Line name="2회차" type="monotone" dataKey="p2" dot={false} stroke="#FFAA00" strokeWidth={3} />
                                                <Line name="3회차" type="monotone" dataKey="p3" dot={false} stroke="#34D399" strokeWidth={3} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </section>

                                <TrialInlineStats items={powerStats} />

                                <section className="mt-4 md:mt-5 rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="px-4 md:px-5 py-3 md:py-4"><h3 className="font-bold md:text-lg">기능 분석</h3></div>
                                    <div className="px-3"><div className="h-px bg-gray-100" /></div>
                                    <div className="px-3 md:px-4 py-3 md:py-4 text-sm md:text-base leading-6 text-[#9F9F9F] whitespace-pre-line">
                                        {powerAnalysisLines.join("\n")}
                                    </div>
                                </section>
                            </>
                        )}

                        {/* ---------------- muscular_endurance ---------------- */}
                        {type === "muscular_endurance" && (
                            <>
                                {/* 같은 날 근지구력 세션 전환 */}
                                {meDayList.length > 1 && (
                                    <div className="flex gap-2 md:gap-3 mb-3">
                                        {meDayList
                                            .slice()
                                            .sort((a, b) => new Date(a.dt) - new Date(b.dt))
                                            .map((s, i) => (
                                                <button
                                                    key={s.id ?? i}
                                                    onClick={() => setMeIdx(i)}
                                                    className={[
                                                        "px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-semibold transition",
                                                        i === meIdx ? "bg-[#5F9BFF] text-white shadow" : "bg-gray-200 text-gray-700",
                                                    ].join(" ")}
                                                >
                                                    세션 {i + 1} · {format(new Date(s.dt), "HH:mm", { locale: ko })}
                                                </button>
                                            ))}
                                    </div>
                                )}

                                <section className="rounded-2xl border border-gray-100 shadow-sm pt-4 md:pt-5 pb-2 md:pb-3 px-3 md:px-4">
                                    <div className="relative h-60 md:h-72 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={enduranceSeries} margin={{ top: 10, right: 8, left: 8, bottom: 8 }}>
                                                <XAxis dataKey="i" tick={false} axisLine={false} tickLine={false} />
                                                <YAxis domain={[0, enduranceYMax]} hide />
                                                <Tooltip formatter={(v) => [`${v}`, "rep peak"]} labelFormatter={(l) => `${l}회차`} />
                                                <Bar dataKey="v" fill="#5F9BFF" radius={[6, 6, 0, 0]} barSize={16} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </section>

                                <section className="mt-4 md:mt-5 rounded-2xl border border-gray-100 shadow-sm overflow-hidden bg-white">
                                    <div className="grid grid-cols-3 gap-4 p-4">
                                        {enduranceStatsTop.map((it, idx) => (
                                            <div key={idx} className="flex flex-col items-center">
                                                <div className="w-full bg-[#B3B7BD] text-white rounded-xl py-3 text-center leading-tight font-bold text-[13px] md:text_base">
                                                    {it.label}
                                                </div>
                                                <div className="mt-3 text-3xl md:text-4xl font-extrabold text-[#5F9BFF]">{it.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                <section className="mt-4 md:mt-5 rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="px-4 md:px-5 py-3 md:py-4"><h3 className="font-bold md:text-lg">기능 분석</h3></div>
                                    <div className="px-3"><div className="h-px bg-gray-100" /></div>
                                    <div className="px-3 md:px-4 py-3 md:py-4 text-sm md:text-base leading-6 text-[#9F9F9F] whitespace-pre-line">
                                        {enduranceAnalysisLines.join("\n")}
                                    </div>
                                </section>
                            </>
                        )}

                        <button
                            onClick={() => navigate(`/workout/day/video`)}
                            type="button"
                            className="w-full bg-[#5F9BFF] hover:bg-[#4A8AFF] text-white rounded-lg py-2 md:py-2.5 active:scale-[.99] transition text-base md:text-lg mt-4 md:mt-5"
                        >
                            운동 처방 영상
                        </button>
                    </div>
                </AuthCard>
            )}
            <BottomNav className="h-16 md:h-[72px]" />
        </main>
    );
}

function InlineStats({ items = [] }) {
    return (
        <section className="mt-4 md:mt-5 rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-stretch divide-x divide-gray-100">
                {items.map((it, i) => (
                    <div key={i} className="flex-1 p-4 md:p-5 text-center bg-white">
                        <div className="text-sm md:text-base text-gray-500">{it.label}</div>
                        <div className="mt-1 text-3xl md:text-4xl font-extrabold text-[#5F9BFF]">{it.value}</div>
                        <div className="text-xs md:text-sm text-gray-400">{it.unit}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function TrialInlineStats({ items = [] }) {
    return (
        <section className="mt-4 md:mt-5 rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-stretch bg-white divide-x divide-gray-100">
                {items.map((it, i) => (
                    <div key={i} className="flex-1 px-4 md:px-5 py-3 md:py-4 text-center">
                        <div
                            className="inline-flex items-center justify-center rounded-lg px-5 py-1 text-white text-sm font-semibold"
                            style={{ backgroundColor: it.color }}
                        >
                            {it.badge}
                        </div>
                        <div className="mt-2 text-3xl md:text-4xl font-extrabold text-[#5F9BFF]">{it.value}</div>
                        <div className="text-xs md:text-sm text-gray-400">{it.unit}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}
